/* Pre-elaborazione — porting fedele di flusso-urgenze.html (714-799).
   Numerazione messaggi, fasi, blocchi/rami, righe (con raggruppamento
   "contemporanei"). Non muta il documento: costruisce oggetti PEvent/PRow/…
   distinti che il layout e il renderer consumano.

   Le ricerche di partecipanti/documenti sono difensive (fallback) così un
   riferimento momentaneamente pendente durante l'editing non fa crashare il
   render — la validazione referenziale vive in validateDiagram(). */
import type { DiagramDoc, Doc, Participant } from "../doc-schema.js";
import { totalWidth } from "./constants.js";
import { plain } from "./markup.js";
import type { Narrow, PBlock, PBranch, PEvent, PPhase, PRow, PreparedModel } from "./model.js";

export function prepare(doc: DiagramDoc): PreparedModel {
  const partIdx = new Map<string, number>();
  const partById = new Map<string, Participant>();
  doc.participants.forEach((p, i) => {
    partIdx.set(p.id, i);
    partById.set(p.id, p);
  });
  const docById = new Map<string, Doc>();
  doc.docs.forEach((d) => docById.set(d.id, d));

  const idxOf = (id: string): number => partIdx.get(id) ?? 0;
  const nameOf = (id: string): string => partById.get(id)?.name ?? id;
  const labelOf = (id: string): string => docById.get(id)?.label ?? id;

  const MSGS: PEvent[] = [];
  const PHASES: PPhase[] = [];
  const BLOCKS: PBlock[] = [];
  const ROWS: PRow[] = [];

  const events: PEvent[] = doc.flow.map((raw, i) => ({
    raw,
    t: raw.t,
    i,
    phase: null,
    block: null,
    branch: null,
    row: null,
    path: [],
  }));

  let phase: PPhase | null = null;
  let rowKey: string | null = null;
  let row: PRow | null = null;
  const stack: PBlock[] = [];

  for (const ev of events) {
    const raw = ev.raw;

    if (raw.t === "phase") {
      phase = { id: raw.id, title: raw.title, sub: raw.sub, num: PHASES.length + 1, msgs: [], ev };
      PHASES.push(phase);
      ev.phase = phase;
      rowKey = null;
      continue;
    }

    ev.phase = phase;
    if (raw.t === "block" || raw.t === "else" || raw.t === "end") rowKey = null;
    if (raw.t === "note") rowKey = null;

    if (raw.t === "block") {
      const first: PBranch = { cond: raw.cond, msgs: [], ev };
      const b: PBlock = {
        kind: raw.kind,
        depth: stack.length,
        branches: [first],
        cols: new Set<number>(),
        ev,
        idx: BLOCKS.length,
        cur: first,
      };
      ev.block = b;
      ev.branch = first;
      stack.push(b);
      BLOCKS.push(b);
      continue;
    }
    if (raw.t === "else") {
      const b = stack[stack.length - 1]!;
      const br: PBranch = { cond: raw.cond, msgs: [], ev };
      b.branches.push(br);
      b.cur = br;
      ev.block = b;
      ev.branch = br;
      continue;
    }
    if (raw.t === "end") {
      const b = stack.pop()!;
      ev.block = b;
      b.endEv = ev;
      continue;
    }

    if (raw.t === "note") {
      for (const b of stack) b.cols.add(idxOf(raw.over));
      const r: PRow = {
        kind: "note",
        ev,
        msgs: [],
        sync: null,
        block: stack[stack.length - 1] ?? null,
        branch: stack.length ? stack[stack.length - 1]!.cur : null,
        phase,
        hasCreate: false,
        hasDestroy: false,
      };
      ev.row = r;
      ROWS.push(r);
      continue;
    }

    if (raw.t === "msg") {
      const n = MSGS.length + 1;
      ev.n = n;
      MSGS.push(ev);
      ev.docs = raw.docs ?? [];
      ev.search = (
        plain(raw.text) +
        " " +
        nameOf(raw.from) +
        " " +
        nameOf(raw.to) +
        " " +
        ev.docs.map(labelOf).join(" ") +
        " " +
        stack.map((b) => b.kind + " " + plain(b.cur.cond)).join(" ")
      ).toLowerCase();
      ev.lines = plain(raw.text).split("\n");
      if (phase) phase.msgs.push(ev);
      for (const b of stack) {
        b.cols.add(idxOf(raw.from));
        b.cols.add(idxOf(raw.to));
        b.cur.msgs.push(ev);
      }
      ev.block = stack.length ? stack[stack.length - 1]! : null;
      ev.branch = stack.length ? stack[stack.length - 1]!.cur : null;
      ev.path = stack.map((b) => ({ kind: b.kind, cond: plain(b.cur.cond) }));
      if (raw.sync && raw.sync === rowKey) {
        row!.msgs.push(ev);
        ev.row = row;
      } else {
        rowKey = raw.sync ?? null;
        row = {
          kind: "msg",
          msgs: [ev],
          sync: raw.sync ?? null,
          block: ev.block,
          branch: ev.branch,
          phase,
          hasCreate: false,
          hasDestroy: false,
        };
        ROWS.push(row);
        ev.row = row;
      }
      continue;
    }

    if (raw.t === "create" || raw.t === "destroy") {
      if (raw.t === "create" && !raw.inline) {
        ev.standalone = true;
        rowKey = null;
        continue;
      }
      if (raw.t === "destroy" && row) {
        row.hasDestroy = true;
        ev.row = row;
      }
      continue;
    }
  }

  /* Pass 2: ogni create si lega alla riga del messaggio che segue (per dare
     altezza extra alla riga che ospita un create inline e per far sparire il
     riquadro dell'attore temporaneo quando quella riga è filtrata). */
  events.forEach((ev, i) => {
    if (ev.raw.t !== "create") return;
    for (let j = i + 1; j < events.length; j++) {
      if (events[j]!.raw.t === "msg") {
        ev.row = events[j]!.row;
        if ((ev.raw as Narrow<"create">).inline && ev.row) ev.row.hasCreate = true;
        break;
      }
    }
  });

  return {
    doc,
    events,
    MSGS,
    PHASES,
    BLOCKS,
    ROWS,
    partIdx,
    partById,
    docById,
    width: totalWidth(doc.participants.length),
    total: MSGS.length,
  };
}
